import { useEffect, useRef } from 'react';

const money = (value) =>
  Number(value || 0).toLocaleString('es-PE', {
    style: 'currency',
    currency: 'PEN',
  });

function formatDate(value) {
  if (!value) return '-';
  if (/^\d{4}-\d{2}-\d{2}/.test(String(value))) {
    const [year, month, day] = String(value).slice(0, 10).split('-');
    return `${day}/${month}/${year}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-PE');
}

function HeaderCheckbox({ checked, indeterminate, disabled, onChange }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500 disabled:cursor-not-allowed disabled:opacity-50"
      aria-label="Seleccionar créditos visibles"
    />
  );
}

export default function GestionCarteraTable({
  rows = [],
  loading = false,
  error = '',
  selectedIds,
  selectedCount = 0,
  maxSelection = 500,
  onToggleRow,
  onTogglePage,
}) {
  const pageIds = rows.map((row) => Number(row.id_prestamo));
  const selectedOnPage = pageIds.filter((id) => selectedIds.has(id)).length;
  const allPageSelected = rows.length > 0 && selectedOnPage === rows.length;
  const somePageSelected = selectedOnPage > 0 && !allPageSelected;
  const disableNewSelection = selectedCount >= maxSelection;

  return (
    <>
      {error ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          {error}
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              <th className="px-3 py-3 text-center">
                <HeaderCheckbox
                  checked={allPageSelected}
                  indeterminate={somePageSelected}
                  disabled={rows.length === 0 || (disableNewSelection && !allPageSelected)}
                  onChange={() => onTogglePage(rows, allPageSelected)}
                />
              </th>
              <th className="px-3 py-3 text-center">CRÉDITO</th>
              <th className="px-3 py-3 text-left">CLIENTE</th>
              <th className="px-3 py-3 text-left">ASESOR ACTUAL</th>
              <th className="px-3 py-3 text-center">MONTO</th>
              <th className="px-3 py-3 text-center">SALDO</th>
              <th className="px-3 py-3 text-center">CUOTAS</th>
              <th className="px-3 py-3 text-center">FRECUENCIA</th>
              <th className="px-3 py-3 text-center">INICIO</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td className="px-4 py-8 text-center text-slate-500" colSpan={9}>
                  {loading ? 'Cargando...' : 'Sin resultados'}
                </td>
              </tr>
            ) : null}

            {rows.map((row) => {
              const id = Number(row.id_prestamo);
              const isSelected = selectedIds.has(id);
              const rowSelectionDisabled = !isSelected && disableNewSelection;

              return (
                <tr
                  key={id}
                  className={[
                    'border-b border-slate-200 transition last:border-b-0',
                    isSelected ? 'bg-red-50/70' : 'odd:bg-white even:bg-slate-50/50 hover:bg-rose-50/40',
                  ].join(' ')}
                >
                  <td className="px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={rowSelectionDisabled}
                      onChange={() => onToggleRow(row)}
                      className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={`Seleccionar crédito ${id}`}
                    />
                  </td>

                  <td className="px-3 py-3 text-center font-semibold text-slate-900">#{id}</td>

                  <td className="px-3 py-3">
                    <div className="flex flex-col">
                      <span className="font-semibold text-slate-900">{row.cliente?.nombre_completo || 'Sin nombre'}</span>
                      <span className="text-xs text-slate-500">{row.cliente?.dni || 'Sin documento'}</span>
                    </div>
                  </td>

                  <td className="px-3 py-3">
                    <div className="flex flex-col">
                      <span className="font-semibold text-slate-800">{row.asesor?.nombre_completo || 'Sin asesor'}</span>
                    </div>
                  </td>

                  <td className="px-3 py-3 text-center font-semibold text-slate-900">{money(row.monto)}</td>
                  <td className="px-3 py-3 text-center font-semibold text-slate-900">{money(row.saldo_pendiente)}</td>

                  <td className="px-3 py-3 text-center">
                    <span className="inline-flex items-center justify-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                      {row.cuotas_pagadas}/{row.cuotas}
                    </span>
                  </td>

                  <td className="px-3 py-3 text-center">
                    <span className="text-xs font-semibold text-slate-700">
                      {String(row.frecuencia || '-').toUpperCase()}
                    </span>
                  </td>

                  <td className="px-3 py-3 text-center text-slate-700">{formatDate(row.fecha_inicio)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {loading && rows.length > 0 ? (
          <div className="p-4">
            <div className="mx-auto h-1 w-32 overflow-hidden rounded bg-slate-100">
              <div className="h-full w-1/2 animate-pulse bg-red-600" />
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}